import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { MaskLines, Eyebrow } from "@/lib/motion";
import { IMG } from "@/data/site";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
        <img src={IMG.heroAbstract} alt="Abstract visual representing human expertise and artificial intelligence" className="h-full w-full object-cover opacity-60" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/50 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/40 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 w-full">
        <div className="max-w-3xl">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            <Eyebrow>Practical AI Education</Eyebrow>
          </motion.div>

          <h1 className="mt-6 font-heading font-semibold tracking-tighter text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
            <MaskLines lines={["Learn AI.", "Build Skills.", <span key="edge" className="text-brand-gradient">Create Your Edge.</span>]} delay={0.2} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 text-lg md:text-xl text-slate-300 leading-relaxed max-w-2xl"
          >
            Practical AI education for creators, professionals, entrepreneurs and beginners — helping you understand AI and turn it into real-world skills.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <a href="#courses" data-testid="hero-explore-btn" className="group inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-light text-white font-semibold px-8 py-4 transition-colors">
              Explore Courses
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a href="#about" data-testid="hero-meet-btn" className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-white/50 text-white font-medium px-8 py-4 transition-colors">
              Meet Suhani
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-12 flex items-center gap-3 text-sm text-slate-400"
          >
            {["Learn", "Practice", "Build", "Apply"].map((w, i) => (
              <span key={w} className="flex items-center gap-3">
                <span className="uppercase tracking-[0.2em] font-medium text-slate-300">{w}</span>
                {i < 3 && <span className="h-1 w-1 rounded-full bg-brand" />}
              </span>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
